import { FormGroup, NumericInput } from '@blueprintjs/core'
import { Button } from 'components/Button'
import { Colors } from 'design/colors'
import { Strings } from 'utils/common'
import { IWritePowerConfigResponse } from 'utils/common-types'
import {
  INavigationProps,
  Edit,
  FORM_WIDTH,
  inputStyle,
  configContainerStyle,
  screenTitleStyle
} from './CStoreEditor'
import { useMemo } from 'react'
import { Tooltip2 } from '@blueprintjs/popover2'

export const PowerScreen = (
  props: INavigationProps & {
    powerConfig: Strings<IWritePowerConfigResponse>
    editPowerConfig: Edit<IWritePowerConfigResponse>
    powerConfigValid: boolean
  }
) => {
  const { powerConfig, editPowerConfig, powerConfigValid, prev, next } = props

  const FORM_GROUP_WIDTH = 300

  const { inequalityErrors, errorFields } = useMemo(() => {
    const inequalityErrors: string[] = []
    const errorFields: Set<string> = new Set()

    const {
      warningOvervoltageThreshold,
      criticalOvervoltageThreshold,
      warningUndervoltageThreshold,
      criticalUndervoltageThreshold,
      warningMotorCurrentThreshold,
      criticalMotorCurrentThreshold
    } = powerConfig

    const warnOV = parseFloat(warningOvervoltageThreshold)
    const critOV = parseFloat(criticalOvervoltageThreshold)
    const warnUV = parseFloat(warningUndervoltageThreshold)
    const critUV = parseFloat(criticalUndervoltageThreshold)
    const warnMC = parseFloat(warningMotorCurrentThreshold)
    const critMC = parseFloat(criticalMotorCurrentThreshold)

    if (!isNaN(warnOV) && !isNaN(critOV) && warnOV >= critOV) {
      inequalityErrors.push(
        'Warning Overvoltage Fault Threshold must be less than Critical Overvoltage Fault Threshold.'
      )
      errorFields.add('warningOvervoltageThreshold')
      errorFields.add('criticalOvervoltageThreshold')
    }

    if (!isNaN(warnUV) && !isNaN(warnOV) && warnUV >= warnOV) {
      inequalityErrors.push(
        'Warning Undervoltage Fault Threshold must be less than Warning Overvoltage Fault Threshold.'
      )
      errorFields.add('warningUndervoltageThreshold')
      errorFields.add('warningOvervoltageThreshold')
    }

    if (!isNaN(warnUV) && !isNaN(critUV) && warnUV <= critUV) {
      inequalityErrors.push(
        'Warning Undervoltage Fault Threshold must be greater than Critical Undervoltage Fault Threshold.'
      )
      errorFields.add('warningUndervoltageThreshold')
      errorFields.add('criticalUndervoltageThreshold')
    }

    if (!isNaN(warnMC) && !isNaN(critMC) && warnMC >= critMC) {
      inequalityErrors.push(
        'Warning Motor Current Fault Threshold must be less than Critical Motor Current Fault Threshold.'
      )
      errorFields.add('warningMotorCurrentThreshold')
      errorFields.add('criticalMotorCurrentThreshold')
    }

    return { inequalityErrors, errorFields }
  }, [powerConfig])

  const hasError = (fieldName: string) => errorFields.has(fieldName)

  return (
    <div style={configContainerStyle}>
      <div style={screenTitleStyle}>Hardware Limits</div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
        <div
          style={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 8
          }}
        >
          <FormGroup
            label={
              <span>
                Critical Motor Current Fault Threshold{' '}
                <span style={{ opacity: 0.6, fontSize: 12 }}>(amps)</span>
              </span>
            }
            labelInfo={<span style={{ color: Colors.RED }}>*</span>}
            style={{ width: FORM_GROUP_WIDTH }}
          >
            <NumericInput
              fill={true}
              value={powerConfig.criticalMotorCurrentThreshold}
              onValueChange={(_, valStr) =>
                editPowerConfig('criticalMotorCurrentThreshold', valStr)
              }
              stepSize={1e-15 /* accept any floating point */}
              minorStepSize={null}
              buttonPosition="none"
              style={{
                ...inputStyle,
                border: hasError('criticalMotorCurrentThreshold')
                  ? `1px solid ${Colors.RED}`
                  : undefined
              }}
            />
          </FormGroup>
          <span
            style={{
              fontWeight: 'bold',
              textAlign: 'center',
              padding: 6
            }}
          >
            {'>'}
          </span>
          <FormGroup
            label={
              <span>
                Warning Motor Current Fault Threshold{' '}
                <span style={{ opacity: 0.6, fontSize: 12 }}>(amps)</span>
              </span>
            }
            labelInfo={<span style={{ color: Colors.RED }}>*</span>}
            style={{ width: FORM_GROUP_WIDTH }}
          >
            <NumericInput
              fill={true}
              value={powerConfig.warningMotorCurrentThreshold}
              onValueChange={(_, valStr) => editPowerConfig('warningMotorCurrentThreshold', valStr)}
              stepSize={1e-15 /* accept any floating point */}
              minorStepSize={null}
              buttonPosition="none"
              style={{
                ...inputStyle,
                border: hasError('warningMotorCurrentThreshold')
                  ? `1px solid ${Colors.RED}`
                  : undefined
              }}
            />
          </FormGroup>
          <Tooltip2
            placement="top"
            intent="success"
            content={
              'It is recommended that the Motor Current Limit be less than the warning current threshold'
            }
          >
            <span
              style={{
                fontWeight: 'bold',
                textAlign: 'center',
                padding: 6,
                color: '#777777',
                cursor: 'help'
              }}
            >
              {'>'}
            </span>
          </Tooltip2>

          <FormGroup
            label={
              <span>
                Motor Current Limit <span style={{ opacity: 0.6, fontSize: 12 }}>(amps)</span>
              </span>
            }
            labelInfo={<span style={{ color: Colors.RED }}>*</span>}
            style={{ width: FORM_GROUP_WIDTH }}
          >
            <NumericInput
              fill={true}
              value={powerConfig.motorCurrentLimit}
              onValueChange={(_, valStr) => editPowerConfig('motorCurrentLimit', valStr)}
              stepSize={1e-15 /* accept any floating point */}
              minorStepSize={null}
              buttonPosition="none"
              style={inputStyle}
            />
          </FormGroup>
          <span
            style={{
              width: 20
            }}
          ></span>
          <FormGroup
            label={
              <span>
                Battery Current Limit <span style={{ opacity: 0.6, fontSize: 12 }}>(amps)</span>
              </span>
            }
            labelInfo={<span style={{ color: Colors.RED }}>*</span>}
            style={{ width: FORM_GROUP_WIDTH }}
          >
            <NumericInput
              fill={true}
              value={powerConfig.batteryCurrentLimit}
              onValueChange={(_, valStr) => editPowerConfig('batteryCurrentLimit', valStr)}
              stepSize={1e-15 /* accept any floating point */}
              minorStepSize={null}
              buttonPosition="none"
              style={inputStyle}
            />
          </FormGroup>
        </div>

        <div
          style={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 8
          }}
        >
          <FormGroup
            label={
              <span>
                Critical Overvoltage Fault Threshold{' '}
                <span style={{ opacity: 0.6, fontSize: 12 }}>(volts)</span>
              </span>
            }
            labelInfo={<span style={{ color: Colors.RED }}>*</span>}
            style={{ width: FORM_GROUP_WIDTH }}
          >
            <NumericInput
              fill={true}
              value={powerConfig.criticalOvervoltageThreshold}
              onValueChange={(_, valStr) => editPowerConfig('criticalOvervoltageThreshold', valStr)}
              stepSize={1e-15 /* accept any floating point */}
              minorStepSize={null}
              buttonPosition="none"
              style={{
                ...inputStyle,
                border: hasError('criticalOvervoltageThreshold')
                  ? `1px solid ${Colors.RED}`
                  : undefined
              }}
            />
          </FormGroup>
          <span
            style={{
              fontWeight: 'bold',
              textAlign: 'center',
              padding: 6
            }}
          >
            {'>'}
          </span>
          <FormGroup
            label={
              <span>
                Warning Overvoltage Fault Threshold{' '}
                <span style={{ opacity: 0.6, fontSize: 12 }}>(volts)</span>
              </span>
            }
            labelInfo={<span style={{ color: Colors.RED }}>*</span>}
            style={{ width: FORM_GROUP_WIDTH }}
          >
            <NumericInput
              fill={true}
              value={powerConfig.warningOvervoltageThreshold}
              onValueChange={(_, valStr) => editPowerConfig('warningOvervoltageThreshold', valStr)}
              stepSize={1e-15 /* accept any floating point */}
              minorStepSize={null}
              buttonPosition="none"
              style={{
                ...inputStyle,
                border: hasError('warningOvervoltageThreshold')
                  ? `1px solid ${Colors.RED}`
                  : undefined
              }}
            />
          </FormGroup>
          <span
            style={{
              fontWeight: 'bold',
              textAlign: 'center',
              padding: 6
            }}
          >
            {'>'}
          </span>
          <FormGroup
            label={
              <span>
                Warning Undervoltage Fault Threshold{' '}
                <span style={{ opacity: 0.6, fontSize: 12 }}>(volts)</span>
              </span>
            }
            labelInfo={<span style={{ color: Colors.RED }}>*</span>}
            style={{ width: FORM_GROUP_WIDTH }}
          >
            <NumericInput
              fill={true}
              value={powerConfig.warningUndervoltageThreshold}
              onValueChange={(_, valStr) => editPowerConfig('warningUndervoltageThreshold', valStr)}
              stepSize={1e-15 /* accept any floating point */}
              minorStepSize={null}
              buttonPosition="none"
              style={{
                ...inputStyle,
                border: hasError('warningUndervoltageThreshold')
                  ? `1px solid ${Colors.RED}`
                  : undefined
              }}
            />
          </FormGroup>
          <span
            style={{
              fontWeight: 'bold',
              textAlign: 'center',
              padding: 6
            }}
          >
            {'>'}
          </span>
          <FormGroup
            label={
              <span>
                Critical Undervoltage Fault Threshold{' '}
                <span style={{ opacity: 0.6, fontSize: 12 }}>(volts)</span>
              </span>
            }
            labelInfo={<span style={{ color: Colors.RED }}>*</span>}
            style={{ width: FORM_GROUP_WIDTH }}
          >
            <NumericInput
              fill={true}
              value={powerConfig.criticalUndervoltageThreshold}
              onValueChange={(_, valStr) =>
                editPowerConfig('criticalUndervoltageThreshold', valStr)
              }
              stepSize={1e-15 /* accept any floating point */}
              minorStepSize={null}
              buttonPosition="none"
              style={{
                ...inputStyle,
                border: hasError('criticalUndervoltageThreshold')
                  ? `1px solid ${Colors.RED}`
                  : undefined
              }}
            />
          </FormGroup>
        </div>
      </div>

      <div
        style={{
          width: FORM_WIDTH,
          maxWidth: '100%',
          marginTop: 20,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center'
        }}
      >
        <FormGroup
          label={
            <span>
              Overtemperature Threshold{' '}
              <span style={{ opacity: 0.6, fontSize: 12 }}>(celsius)</span>
            </span>
          }
          labelInfo={<span style={{ color: Colors.RED }}>*</span>}
          style={{ width: FORM_GROUP_WIDTH }}
        >
          <NumericInput
            fill={true}
            value={powerConfig.overtemperatureThreshold}
            onValueChange={(_, valStr) => editPowerConfig('overtemperatureThreshold', valStr)}
            stepSize={1e-15 /* accept any floating point */}
            minorStepSize={null}
            buttonPosition="none"
            style={inputStyle}
          />
        </FormGroup>
        {!powerConfigValid && inequalityErrors.length > 0 && (
          <FormGroup>
            <ul style={{ color: Colors.RED, fontWeight: 700 }}>
              {inequalityErrors.map((err, idx) => (
                <li key={idx}>{err}</li>
              ))}
            </ul>
          </FormGroup>
        )}
        <FormGroup
          label={
            <span style={{ fontWeight: 900 }}>
              The fault thresholds will not take effect until a power cycle.
            </span>
          }
        ></FormGroup>
      </div>
      <div style={{ display: 'flex', width: FORM_WIDTH }}>
        <div style={{ flex: 1 }}>
          <Button
            backgroundColor={Colors.WHITE}
            textColor={Colors.BLACK}
            onClick={prev}
            disabled={prev === undefined}
          >
            Back
          </Button>
        </div>
        <div style={{ width: 8 }} />
        <div style={{ flex: 1 }} />
        <div style={{ width: 8 }} />
        <div style={{ flex: 1 }}>
          <Button
            backgroundColor={Colors.BUTTON_GREEN}
            textColor={Colors.WHITE}
            onClick={next}
            disabled={next === undefined}
          >
            Next
          </Button>
        </div>
      </div>
    </div>
  )
}
